import type {
	PresentationRichTextBlock,
	PresentationRichTextInline,
	PresentationRichTextNode,
} from "./presentation-rich-text-v1";

/** A TipTap (ProseMirror) JSON node, as `editor.getJSON()` emits and `setContent` accepts. */
export interface TiptapJsonNode {
	type: string;
	attrs?: Record<string, unknown>;
	content?: TiptapJsonNode[];
	marks?: Array<{ type: string; attrs?: Record<string, unknown> }>;
	text?: string;
}

function inlineToTiptap(inline: PresentationRichTextInline): TiptapJsonNode {
	if (inline.type === "hardBreak") {
		return { type: "hardBreak" };
	}
	return {
		type: "text",
		text: inline.text,
		...(inline.marks?.length
			? { marks: inline.marks.map((type) => ({ type })) }
			: {}),
	};
}

function nodeToTiptap(node: PresentationRichTextNode): TiptapJsonNode {
	// Stored rich text may hold empty text nodes, which TipTap refuses (and sanitize drops).
	const children: TiptapJsonNode[] =
		node.type === "paragraph"
			? (node.content ?? [])
					.filter(
						(inline) =>
							inline.type !== "text" || inline.text.length > 0,
					)
					.map(inlineToTiptap)
			: (node.content ?? []).map((child) => nodeToTiptap(child));
	// TipTap leaves `content` off an empty node.
	return children.length > 0
		? { type: node.type, content: children }
		: { type: node.type };
}

/**
 * Presentation rich text blocks → TipTap JSON (marks as `{ type }` objects, no empty `content`),
 * the inverse of `tiptapDocToPresentationRichText` for the nodes and marks both allow.
 */
export function presentationRichTextToTiptapContent(
	blocks: readonly PresentationRichTextBlock[],
): TiptapJsonNode[] {
	return blocks.map(nodeToTiptap);
}
